<template>
  <div v-if="unavailableRooms.length">
    <div v-if="type !== 'emptySearch'" class="my-10 flex flex-col items-center">
      <div class="font-medium">Limited availability for your dates.</div>
      <div class="font-medium text-center">
        Check with us to find available dates for your desired villa.
      </div>
      <trengo-link>
        <template v-slot="{ openChat }">
          <button
            type="button"
            class="btn btn-primary btn-outlined flex items-center sm:justify-center mt-4"
            @click="openChat()"
          >
            <svg width="24" height="24" class="fill-current mr-2">
              <use xlink:href="/img/sprite.svg#ic_chat"></use>
            </svg>
            Chat with us
          </button>
        </template>
      </trengo-link>
    </div>

    <section class="mt-5 sm:mt-0 unavailable-rooms-container">
      <article
        v-for="room in unavailableRooms"
        :key="room.id"
        class="wrap-unavailable-room rounded"
        :class="{ 'unavailable-room-only-chat': !!hbtFlowChatOnly }"
      >
        <h4 class="fs-20 mb-4 font-medium sm:hidden">{{ room.name }}</h4>
        <div class="wrap-room">
          <div class="w-full wrap-image relative">
            <div
              @click="$emit('select', room)"
              class="w-full relative cursor-pointer"
            >
              <room-image-carousel :images="room.gallery"></room-image-carousel>
            </div>
          </div>
          <div class="room-content flex-1 items-end">
            <div class="flex flex-col">
              <h4
                class="fs-16 font-medium mb-3 sm:hidden"
                :class="{ 'text-black-light-3': type !== 'emptySearch' }"
              >
                Room details
              </h4>
              <h4
                class="room-name fs-16 mb-4 sm:mb-2 font-medium lg:hidden md:hidden"
              >
                {{ room.name }}
              </h4>
              <div class="flex flex-col gap-2 sm:flex-row sm:gap-4">
                <div
                  v-if="room.bed_type"
                  class="flex items-center"
                  :class="{ 'text-black-light-3': type !== 'emptySearch' }"
                >
                  <svg width="24" height="24" class="black flex-none">
                    <use xlink:href="/img/sprite.svg#ic_double-bed" />
                  </svg>
                  <span class="ml-2">
                    {{ room.bed_type }}
                  </span>
                </div>
                <div
                  v-if="room.baseOccupancy"
                  class="flex items-center"
                  :class="{ 'text-black-light-3': type !== 'emptySearch' }"
                >
                  <svg width="24" height="24">
                    <use xlink:href="/img/sprite.svg#ic_people"></use>
                  </svg>
                  <span class="ml-2"> {{ room.baseOccupancy }} people </span>
                </div>
                <div
                  v-if="room.room_size_metric"
                  class="flex items-center sm:hidden"
                  :class="{ 'text-black-light-3': type !== 'emptySearch' }"
                >
                  <svg width="24" height="24" class="black">
                    <use xlink:href="/img/sprite.svg#ic_resort" />
                  </svg>
                  <span class="ml-2"> {{ room.room_size_metric }} sqm </span>
                </div>
                <div
                  v-if="childrenNotAllowed(room)"
                  class="flex items-center sm:hidden"
                  :class="{ 'text-black-light-3': type !== 'emptySearch' }"
                >
                  <svg width="24" height="24" class="black">
                    <use xlink:href="/img/sprite.svg#ic_adult-only" />
                  </svg>
                  <span class="ml-2"> Adults only </span>
                </div>
              </div>
              <div
                class="text-secondary font-medium cursor-pointer mt-2 w-fit-content"
                @click="$emit('select', room)"
              >
                View room details
              </div>
            </div>
            <template v-if="type === 'emptySearch'">
              <div
                v-if="!hbtFlowChatOnly"
                @click="$emit('detail-for-pricing')"
                class="btn btn-secondary w-50 sm:hidden py-3 h-fit-content text-center cursor-pointer sm:w-full"
              >
                Show prices
              </div>
              <responsive v-else>
                <template #lg>
                  <chat-with-us-now :room="room" />
                </template>
              </responsive>
            </template>
            <div v-else class="sold-out sm:mt-4 font-bold">Sold out</div>
          </div>
        </div>
        <div
          v-if="type === 'emptySearch' && !hbtFlowChatOnly"
          @click="$emit('detail-for-pricing')"
          class="btn btn-secondary lg:hidden md:hidden py-3 h-fit-content text-center cursor-pointer w-full"
        >
          Show prices
        </div>

        <div
          v-if="room && room.price"
          class="text-right sm:text-left mt-6 md:hidden lg:hidden"
        >
          <currency-context>
            <template v-slot="{ convert }">
              from
              <span class="font-bold fs-16" v-text="convert(room.price)"></span>
              /night
            </template>
          </currency-context>
        </div>
      </article>
    </section>

    <responsive>
      <template #sm>
        <div class="wrap-chat-with-us">
          <chat-with-us-now />
        </div>
      </template>
    </responsive>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import RoomDetailsModal from "./RoomDetailsModal.vue";
import RoomImageCarousel from "./RoomImageCarousel.vue";
import TrengoLink from "../../core/TrengoLink";
import ChatWithUsNow from "./ChatWithUsNow.vue";
import Responsive from "../../core/Responsive";
import CurrencyContext from "../../core/CurrencyContext";

export default {
  components: {
    Responsive,
    RoomImageCarousel,
    RoomDetailsModal,
    TrengoLink,
    ChatWithUsNow,
    CurrencyContext,
  },
  props: ["type"],
  computed: {
    ...mapState(["supplierInfo", "groupsResult"]),
    ...mapGetters(["getHbtRoom", "hbtFlowChatOnly"]),
    selectedGroupId: {
      get() {
        return this.$store.state.selectedGroupId;
      },
      set(value) {
        this.$store.commit("setSelectedGroupId", value);
      },
    },
    roomsForCurrentSelectedGroup() {
      return this.groupsResult
        .find((group) => group.id === this.selectedGroupId)
        .rooms.filter((room) => {
          return Boolean(this.getHbtRoom(room.code));
        });
    },
    unavailableRooms() {
      if (this.type === "emptySearch") {
        return this.supplierInfo.supplier.hbt_rooms.sort((a, b) => {
          return a.price - b.price;
        });
      }

      const result = [];
      this.supplierInfo.supplier.hbt_rooms
        .sort((a, b) => a.price - b.price)
        .forEach((hbtRoom) => {
          if (
            !this.roomsForCurrentSelectedGroup.some(
              (room) => room.code === hbtRoom.code
            )
          ) {
            result.push(hbtRoom);
          }
        });
      return result;
    },
  },
  methods: {
    childrenNotAllowed(roomInfo) {
      if (
        roomInfo.childrenAllowed === undefined ||
        roomInfo.childrenAllowed === null
      ) {
        return false;
      }

      return !roomInfo.childrenAllowed;
    },
  },
};
</script>

<style scoped>
.sold-out {
  text-align: end;
  color: #e32d26;
}

.wrap-unavailable-room {
  padding: 32px 50px 32px 32px;
  border: 1px solid #dcdcdc;
  border-top: none;
}

.unavailable-room-only-chat {
  padding: 32px 16px;
}

.wrap-unavailable-room:first-child {
  border-top: 1px solid #dcdcdc;
}

.wrap-room {
  display: grid;
  grid-template-columns: 300px 1.3fr;
  column-gap: 2rem;
}

.room-content {
  display: flex;
  justify-content: space-between;
}

@media (max-width: 600px) {
  .wrap-unavailable-room {
    background-color: white;
    box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.16);
    padding: 1.5rem 1rem;
    margin-bottom: 24px;
    border: unset !important;
  }

  .wrap-image {
    width: 100%;
  }

  .room-name {
    font-size: 20px;
  }

  .wrap-room {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .room-content {
    display: flex;
    flex-direction: column;
    align-items: start;
    gap: 20px;
  }

  .wrap-room ::v-deep .VueCarousel,
  .wrap-room ::v-deep .VueCarousel-inner {
    height: 100% !important;
  }

  .wrap-room ::v-deep .VueCarousel-wrapper {
    overflow-x: hidden !important;
  }

  .wrap-room ::v-deep .VueCarousel-slide {
    max-width: unset;
  }

  .unavailable-rooms-container {
    background-color: #f6f6f6;
  }
}
</style>
